import { useAdminStore } from '@admin/AppProvider'
import { TemperatureUnit, TenantDto, TerminalType } from '@admin/app/api'
import Permission from '@admin/app/models/permission.enum'
import {
  Button,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  EmptyIndicator,
  Header,
  HighlightedText,
  InfoIcon,
  OptionsButton,
  PlusIcon,
  TomTable,
  tomTableColumnFilterPresets,
  TomTableEmptyCell,
  useDefaultMRTOptions,
  useMinimalsTheme,
  useTomTable,
} from '@tom-ui/ui'
import { PATHS, useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useNavigate } from 'react-router'

export const TenantsContainer = observer(() => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const navigate = useNavigate()
  const { tenantUIStore, tenantDrawerUIStore, timezonesStore, authStore } = useAdminStore()
  const canEdit = authStore.hasPermission(Permission.WriteTenants)
  const defaultOptions = useDefaultMRTOptions<TenantDto>()

  const getActiveConfigurations = (tenant: TenantDto) => {
    const activeConfigs: string[] = []

    if (tenant.configs) {
      for (const key in tenant.configs) {
        if (Object.prototype.hasOwnProperty.call(tenant.configs, key)) {
          const element = (tenant.configs as any)[key]
          const notBooleanConfigs = ['currentOperatorViewSettings', 'selectedOperatorViewConfigs']

          if (element && !notBooleanConfigs.includes(key)) {
            activeConfigs.push(t(key))
          }
        }
      }
    }

    return activeConfigs
  }

  const columns = useMemo<MRT_ColumnDef<TenantDto>[]>(
    () => [
      {
        ...tomTableColumnFilterPresets.searchMandatory(),
        accessorKey: 'id',
        header: t('id', 'Id'),
      },
      {
        ...tomTableColumnFilterPresets.searchMandatory(),
        accessorKey: 'name',
        header: t('name', 'Name'),
      },
      {
        ...tomTableColumnFilterPresets.searchOptional(),
        accessorKey: 'timezoneId',
        header: t('timezone', 'Time Zone'),
        accessorFn: row =>
          row.timezoneId ? timezonesStore.timezones.find(tz => tz.id === row.timezoneId)?.name : '',
        Cell: TomTableEmptyCell,
      },
      {
        ...tomTableColumnFilterPresets.searchOptional(),
        accessorKey: 'cultureId',
        header: t('language', 'Language'),
        accessorFn: row =>
          `${row.cultureId ? timezonesStore.cultures.find(c => c.id === row.cultureId)?.name : ''}`,
        Cell: TomTableEmptyCell,
      },
      {
        accessorKey: 'temperatureUnit',
        header: t('temperatureUnit', 'Temperature unit'),
        accessorFn: row => `${row.temperatureUnit ? t(row.temperatureUnit) : EmptyIndicator}`,
        Cell: TomTableEmptyCell,
        filterVariant: 'select',
        filterSelectOptions: Object.values(TemperatureUnit).map(value => ({
          label: t(value, value),
          value,
        })),
        filterFn: 'equals',
        enableColumnFilterModes: false,
      },
      {
        ...tomTableColumnFilterPresets.searchMandatory(),
        accessorKey: 'unloCodes',
        header: t('terminalLocationCode', 'Terminal location code'),
        minSize: 250,
        accessorFn: row => row.unloCodes.map(code => code.name).join(', '),
        Cell: cell => {
          const unlocodes = cell.row.original.unloCodes
          const filterValue = cell.column.getFilterValue() as string | undefined
          return (
            <Stack
              direction='row'
              gap={theme.customSpacing.xs}
              flexWrap='wrap'
              py={theme.customSpacing.xs}
            >
              {unlocodes?.map(item => (
                <Chip
                  key={`unlocodes-${item.name}`}
                  size='small'
                  label={
                    <HighlightedText highlight={filterValue} color={theme.palette.info.main}>
                      {item.name}
                    </HighlightedText>
                  }
                />
              ))}
            </Stack>
          )
        },
      },
      {
        ...tomTableColumnFilterPresets.searchMandatory(),
        accessorKey: 'configs.skipCraneSplit',
        header: t('activeConfigurations', 'Active configurations'),
        minSize: 250,
        maxSize: 600,
        accessorFn: row => getActiveConfigurations(row),
        Cell: cell => {
          const configurations = getActiveConfigurations(cell.row.original)
          const filterValue = cell.column.getFilterValue() as string | undefined
          if (configurations.length <= 2) {
            return (
              <Stack flexDirection={'row'} gap={1} flexWrap='wrap'>
                {configurations.map(config => (
                  <Chip
                    size='small'
                    label={
                      <HighlightedText highlight={filterValue} color={theme.palette.info.main}>
                        {t(config)}
                      </HighlightedText>
                    }
                  />
                ))}
              </Stack>
            )
          } else {
            return (
              <Stack flexDirection={'row'} gap={1} flexWrap='wrap'>
                <Chip
                  size='small'
                  label={
                    <HighlightedText highlight={filterValue} color={theme.palette.info.main}>
                      {t(configurations[0])}
                    </HighlightedText>
                  }
                />
                <Chip
                  size='small'
                  label={
                    <HighlightedText highlight={filterValue} color={theme.palette.info.main}>
                      {t(configurations[1])}
                    </HighlightedText>
                  }
                />
                <Chip
                  size='small'
                  label={t('plusCount', '+{count}', {
                    count: configurations.length - 2,
                  })}
                />
              </Stack>
            )
          }
        },
      },
      {
        ...tomTableColumnFilterPresets.searchMandatory(),
        accessorKey: 'configs.operatorViewConfigs',
        header: t('operatorView', 'Operator view settings'),
        accessorFn: (dto: TenantDto) => {
          return dto.configs?.selectedOperatorViewConfigs || ''
        },
        Cell: cell => {
          switch (cell.row.original.configs?.selectedOperatorViewConfigs) {
            case TerminalType.Hig:
              return t('default', 'Default')

            case TerminalType.Inland:
              return (
                <>
                  {t('inland', 'Inland')}
                  <IconButton
                    onClick={() =>
                      tenantDrawerUIStore.openPopup(
                        cell.row.original.configs?.currentOperatorViewSettings ?? '',
                      )
                    }
                  >
                    <InfoIcon />
                  </IconButton>
                </>
              )
            default:
              return t('unknown', 'Unknown')
          }
        },
        filterVariant: 'select',
        filterSelectOptions: [
          { label: t('default', 'Default'), value: TerminalType.Hig },
          { label: t('inland', 'Inland'), value: TerminalType.Inland },
          { label: t('unknown', 'Unknown'), value: TerminalType.Unknown },
        ],
        filterFn: 'equals',
        enableColumnFilterModes: false,
      },
    ],
    [t, timezonesStore, tenantDrawerUIStore],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig('admin/tenants')

  const table = useTomTable<TenantDto>(
    {
      columns,
      data: tenantUIStore.tenants,
      enableTopToolbar: false,
      enableRowSelection: false,
      enableExpanding: false,
      enableSelectAll: false,
      layoutMode: 'semantic',
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 160px)',
          minHeight: 'calc(100vh - 160px)',
        },
      },
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = []
        if (canEdit) {
          options.push(
            {
              label: t('edit', 'Edit'),
              onClick: () => {
                navigate(`${PATHS.config.tenants.url}/${row.original.id}`)
              },
            },
            {
              label: t('remove', 'Remove'),
              onClick: () => tenantUIStore.setToBeRemoveTenantId(row.original.id),
            },
          )
        }

        if (!options.length) return null

        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='user-options-btn'
          />
        )
      },
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: [...defaultOptions.initialState?.columnPinning?.left!, 'name'],
        },
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <>
      <Header
        title={t('tenants', 'Tenants')}
        rightRenderOption={
          <Stack flexDirection='row' gap={theme.customSpacing.xs}>
            {canEdit && (
              <Button
                color='inherit'
                variant='contained'
                sx={{ flex: 'none' }}
                startIcon={<PlusIcon />}
                onClick={() => navigate(`${PATHS.config.tenants.url}/new`)}
              >
                {t('addNew', 'Add new')}
              </Button>
            )}
          </Stack>
        }
      />

      <Container
        sx={{
          paddingY: theme.customSpacing.m,
        }}
      >
        <TomTable table={table} />
      </Container>

      <Dialog
        fullWidth
        maxWidth='lg'
        open={tenantDrawerUIStore.isPopupOpen}
        onClose={() => tenantDrawerUIStore.closePopup()}
      >
        <DialogTitle>{t('operatorView', 'Operator view settings')}</DialogTitle>
        <DialogContent>
          <pre style={{ fontFamily: 'inherit' }}>
            <Typography sx={{ p: 2 }}>{tenantDrawerUIStore.popupText}</Typography>
          </pre>
        </DialogContent>
      </Dialog>
    </>
  )
})
